import { useQuery } from 'react-query';
import { ValueOf } from 'type-fest';

import { QueryFunction, useRequest, type Address } from '@frontend/api';

export const SHIPPING_METHOD = {
  EARLY_OVERNIGHT: 'early_am_overnight',
  GROUND: 'ground',
  ONE_DAY: 'next_day_air',
  TWO_DAY: 'second_day_air',
  EXPEDITED: 'expedited',
} as const;

// Type for shipping methods
export type ShippingMethod = ValueOf<typeof SHIPPING_METHOD>;

export const SHIPPING_METHOD_LABEL = {
  [SHIPPING_METHOD.GROUND]: 'Ground',
  [SHIPPING_METHOD.ONE_DAY]: 'Next Day Air',
  [SHIPPING_METHOD.TWO_DAY]: '2nd Day Air',
  [SHIPPING_METHOD.EARLY_OVERNIGHT]: 'Early AM Overnight',
  [SHIPPING_METHOD.EXPEDITED]: 'Expedited',
} as const satisfies Record<ShippingMethod, string>;

export type UseGetShippingRatesParams = {
  quoteKey: string;
  address: Address;
  itemGroups: Record<
    string,
    {
      quantityId: string;
      expediteId: string | null;
      addOnCells: never[];
    }[]
  >;
};

export type UseGetShippingRatesData = {
  shippingRates: Record<
    string,
    {
      arrivesOn: string;
      method: ShippingMethod;
      price: number;
      taxRate?: number; // not sure if this exists
      token: string;
    }[]
  >;
};

export const buildGetShippingRatesQueryKey = (
  params: UseGetShippingRatesParams,
) => ['getShippingRates', params.quoteKey, params.address, params.itemGroups];

export type UseGetShippingRatesQueryKey = ReturnType<
  typeof buildGetShippingRatesQueryKey
>;

export const useGetShippingRates: QueryFunction<
  UseGetShippingRatesData,
  [UseGetShippingRatesParams],
  UseGetShippingRatesQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: buildGetShippingRatesQueryKey(params),
    queryFn: ({ signal }) =>
      request(`/quotes/key/${params.quoteKey}/shipping_rates`, {
        init: {
          method: 'POST',
          body: {
            address: params.address,
            itemGroups: Object.entries(params.itemGroups).map(
              ([name, items]) => ({
                name,
                items,
              }),
            ),
          },
          signal,
        },
      }),
  });
};
